import React, { useState, useEffect } from 'react';
import { db } from '../../../db/firebase';
import { 
    collection,  
    updateDoc, 
    doc, 
    onSnapshot, 
    orderBy, 
    query,
} from 'firebase/firestore';
import { Search, Mail, Clock, CheckCircle, AlertCircle, Eye } from 'lucide-react';

const ContactSubmissions = () => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [subjectFilter, setSubjectFilter] = useState('all');
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [showDetails, setShowDetails] = useState(false);

    // Fetch contact submissions from Firebase
    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                const submissionsRef = collection(db, 'contactSubmissions');
                const q = query(submissionsRef, orderBy('createdAt', 'desc'));
                
                // Real-time listener for submissions
                const unsubscribe = onSnapshot(q, (snapshot) => {
                    const submissionsData = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    setSubmissions(submissionsData);
                    setLoading(false);
                });

                return unsubscribe;
            } catch (error) {
                console.error('Error fetching submissions:', error);
                setLoading(false);
            }
        };

        fetchSubmissions();
    }, []);

    // Update submission status
    const updateSubmissionStatus = async (submissionId, newStatus) => {
        try {
            const submissionRef = doc(db, 'contactSubmissions', submissionId);
            await updateDoc(submissionRef, {
                status: newStatus,
                read: newStatus === 'read',
                updatedAt: new Date()
            });
        } catch (error) {
            console.error('Error updating submission status:', error);
            alert('Error updating submission status: ' + error.message);
        }
    };

    // Mark as read/unread
    const toggleReadStatus = async (submissionId, currentReadStatus) => {
        try {
            const submissionRef = doc(db, 'contactSubmissions', submissionId);
            await updateDoc(submissionRef, {
                read: !currentReadStatus,
                status: !currentReadStatus ? 'read' : 'new',
                updatedAt: new Date()
            });
        } catch (error) {
            console.error('Error updating read status:', error);
            alert('Error updating read status: ' + error.message);
        }
    };

    // Filter submissions based on search, status, and subject
    const filteredSubmissions = submissions.filter(submission => {
        const matchesSearch = 
            submission.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            submission.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            submission.message?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
        const matchesSubject = subjectFilter === 'all' || submission.subject === subjectFilter;

        return matchesSearch && matchesStatus && matchesSubject;
    });

    // Get status badge color
    const getStatusColor = (status, read) => {
        if (!read) return 'bg-red-100 text-red-800 border border-red-200';
        
        switch (status) {
            case 'new':
                return 'bg-blue-100 text-blue-800 border border-blue-200';
            case 'read':
                return 'bg-green-100 text-green-800 border border-green-200';
            case 'replied':
                return 'bg-purple-100 text-purple-800 border border-purple-200';
            case 'resolved':
                return 'bg-gray-100 text-gray-800 border border-gray-200';
            default:
                return 'bg-gray-100 text-gray-800 border border-gray-200';
        }
    };

    // Get status icon
    const getStatusIcon = (status, read) => {
        if (!read) return <AlertCircle size={14} />;
        
        switch (status) {
            case 'new':
                return <Clock size={14} />;
            case 'read':
                return <Mail size={14} />;
            case 'replied':
                return <CheckCircle size={14} />;
            case 'resolved':
                return <CheckCircle size={14} />;
            default:
                return <Mail size={14} />;
        }
    };

    // Format date
    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        
        try {
            let date;
            
            if (dateValue.toDate) {
                date = dateValue.toDate();
            } else if (dateValue.seconds) {
                date = new Date(dateValue.seconds * 1000);
            } else if (typeof dateValue === 'string') {
                date = new Date(dateValue);
            } else {
                date = new Date(dateValue);
            }
            
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Date Error';
        }
    };

    // Get subject display name
    const getSubjectDisplayName = (subject) => {
        const subjectMap = {
            'product-inquiry': 'Product Inquiry',
            'order-support': 'Order Support',
            'wholesale': 'Wholesale Inquiry',
            'custom-order': 'Custom Order',
            'complaint': 'Complaint',
            'other': 'Other'
        };
        return subjectMap[subject] || subject || 'Not Specified';
    };

    // View submission details
    const viewSubmissionDetails = (submission) => {
        setSelectedSubmission(submission);
        setShowDetails(true);
        
        // Mark as read when viewing details
        if (!submission.read) {
            updateSubmissionStatus(submission.id, 'read');
        }
    };

    // Statistics
    const stats = {
        total: submissions.length,
        new: submissions.filter(s => !s.read).length,
        replied: submissions.filter(s => s.status === 'replied').length,
        resolved: submissions.filter(s => s.status === 'resolved').length
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="h-24 bg-gray-300 rounded"></div>
                            ))}
                        </div>
                        <div className="space-y-4">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="h-20 bg-gray-300 rounded"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                " "
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Contact Submissions</h1>
                    <p className="text-gray-600">Manage and respond to customer inquiries and messages</p>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Submissions</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                            </div>
                            <div className="bg-blue-100 p-3 rounded-full">
                                <Mail className="text-blue-600" size={24} />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">New Messages</p>
                                <p className="text-2xl font-bold text-red-600">{stats.new}</p>
                            </div>
                            <div className="bg-red-100 p-3 rounded-full">
                                <AlertCircle className="text-red-600" size={24} />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Replied</p>
                                <p className="text-2xl font-bold text-purple-600">{stats.replied}</p>
                            </div>
                            <div className="bg-purple-100 p-3 rounded-full">
                                <CheckCircle className="text-purple-600" size={24} />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Resolved</p>
                                <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
                            </div>
                            <div className="bg-green-100 p-3 rounded-full">
                                <CheckCircle className="text-green-600" size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                    <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                        <div className="flex-1 w-full md:w-auto">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Search by name, email, or message..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        
                        <div className="flex gap-4 w-full md:w-auto">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="new">New</option>
                                <option value="read">Read</option>
                                <option value="replied">Replied</option>
                                <option value="resolved">Resolved</option>
                            </select>

                            <select
                                value={subjectFilter}
                                onChange={(e) => setSubjectFilter(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Subjects</option>
                                <option value="product-inquiry">Product Inquiry</option>
                                <option value="order-support">Order Support</option>
                                <option value="wholesale">Wholesale</option>
                                <option value="custom-order">Custom Order</option>
                                <option value="complaint">Complaint</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Submissions Table */}
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Customer
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Subject
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Message Preview
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredSubmissions.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                            No submissions found matching your criteria.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredSubmissions.map((submission) => (
                                        <tr 
                                            key={submission.id} 
                                            className={`hover:bg-gray-50 ${!submission.read ? 'bg-blue-50' : ''}`}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {submission.name}
                                                        {!submission.read && (
                                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                New
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {submission.email}
                                                    </div>
                                                    {submission.phone && (
                                                        <div className="text-sm text-gray-500">
                                                            {submission.phone}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900 font-medium">
                                                    {getSubjectDisplayName(submission.subject)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900 max-w-xs truncate">
                                                    {submission.message}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-2">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(submission.status, submission.read)}`}>
                                                        {getStatusIcon(submission.status, submission.read)}
                                                        <span className="ml-1 capitalize">
                                                            {submission.read ? submission.status || 'read' : 'new'}
                                                        </span>
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(submission.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center space-x-3">
                                                    <button
                                                        onClick={() => viewSubmissionDetails(submission)}
                                                        className="text-blue-600 hover:text-blue-900 transition-colors flex items-center space-x-1"
                                                    >
                                                        <Eye size={16} />
                                                        <span>View</span>
                                                    </button>
                                                    
                                                    <select
                                                        value={submission.status || 'new'}
                                                        onChange={(e) => updateSubmissionStatus(submission.id, e.target.value)}
                                                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    >
                                                        <option value="new">New</option>
                                                        <option value="read">Read</option>
                                                        <option value="replied">Replied</option>
                                                        <option value="resolved">Resolved</option>
                                                    </select>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Submission Details Modal */}
                {showDetails && selectedSubmission && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold text-gray-900">
                                        Contact Submission Details
                                    </h2>
                                    <button
                                        onClick={() => setShowDetails(false)}
                                        className="text-gray-400 hover:text-gray-600 transition-colors text-2xl"
                                    >
                                        ×
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Customer Information */}
                                    <div className="space-y-6">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
                                            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                                <div>
                                                    <strong>Name:</strong> {selectedSubmission.name}
                                                </div>
                                                <div>
                                                    <strong>Email:</strong> {selectedSubmission.email}
                                                </div>
                                                {selectedSubmission.phone && (
                                                    <div>
                                                        <strong>Phone:</strong> {selectedSubmission.phone}
                                                    </div>
                                                )}
                                                <div>
                                                    <strong>Subject:</strong> {getSubjectDisplayName(selectedSubmission.subject)}
                                                </div>
                                                <div>
                                                    <strong>Status:</strong> 
                                                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedSubmission.status, selectedSubmission.read)}`}>
                                                        {selectedSubmission.read ? selectedSubmission.status || 'read' : 'new'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Quick Actions */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h3>
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    onClick={() => {
                                                        window.open(`mailto:${selectedSubmission.email}?subject=Re: ${getSubjectDisplayName(selectedSubmission.subject)}`, '_blank');
                                                        updateSubmissionStatus(selectedSubmission.id, 'replied');
                                                    }}
                                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
                                                >
                                                    Reply via Email
                                                </button>
                                                <button
                                                    onClick={() => toggleReadStatus(selectedSubmission.id, selectedSubmission.read)}
                                                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors text-sm"
                                                >
                                                    {selectedSubmission.read ? 'Mark as Unread' : 'Mark as Read'}
                                                </button>
                                                <select
                                                    value={selectedSubmission.status || 'new'}
                                                    onChange={(e) => updateSubmissionStatus(selectedSubmission.id, e.target.value)}
                                                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                                                >
                                                    <option value="new">New</option>
                                                    <option value="read">Read</option>
                                                    <option value="replied">Replied</option>
                                                    <option value="resolved">Resolved</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Message Content */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Message</h3>
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <p className="text-gray-700 whitespace-pre-wrap">
                                                {selectedSubmission.message}
                                            </p>
                                        </div>

                                        {/* Submission Details */}
                                        <div className="mt-6">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Submission Details</h3>
                                            <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                                                <div>
                                                    <strong>Submitted:</strong> {formatDate(selectedSubmission.createdAt)}
                                                </div>
                                                {selectedSubmission.updatedAt && (
                                                    <div>
                                                        <strong>Last Updated:</strong> {formatDate(selectedSubmission.updatedAt)}
                                                    </div>
                                                )}
                                                <div>
                                                    <strong>Submission ID:</strong> {selectedSubmission.id}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        onClick={() => setShowDetails(false)}
                                        className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactSubmissions;